import { AuthResponse } from '../../types';
import { IAppointment } from './appointment';

/**
 * Online booking -- a visitor with no account choosing a time on a clinic's own website, then
 * confirming from the link emailed to them. Every shape here mirrors `com.carefull.onlinebooking`
 * on the API, and goes with it if the feature is removed.
 */

/** A therapist as the website shows them to anybody: a name and a face, never an address. */
export interface IPublicTherapist {
    id: number;
    firstName: string;
    lastName: string;
    pictureUrl?: string | null;
}

/**
 * Which wording of the data-processing agreement the visitor was shown, stored beside their
 * consent. Bump it whenever the `evaluation_gdpr_paragraph_*` copy changes.
 */
export const PUBLIC_BOOKING_CONSENT_VERSION: string = '2026-09';

/** What the form sends. No password -- that is chosen on the page the emailed link opens. */
export interface IBookingRequestSubmission {
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    note?: string;
    serviceId: number;
    therapistId: number;
    startsAt: string;
    gdprConsent: boolean;
    termsAccepted: boolean;
    consentVersion: string;
    language?: string;
    /** The honeypot. Always empty from a person. */
    website: string;
}

/** The one answer a submission ever gets, so the form cannot reveal who has an account. */
export interface IBookingRequestAccepted {
    status: 'CHECK_EMAIL';
    validForHours: number;
}

export type AccountMode = 'CHOOSE_PASSWORD' | 'ENTER_PASSWORD';

export type VerificationState = 'READY' | 'EXPIRED' | 'USED' | 'INVALID';

/** What the confirmation page draws before anything is typed. */
export interface IVerificationPreview {
    state: VerificationState;
    mode?: AccountMode;
    emailAddress?: string;
    serviceNameRo?: string | null;
    serviceNameEn?: string | null;
    therapist?: IPublicTherapist | null;
    startsAt?: string;
    endsAt?: string;
}

export type BookingOutcome =
    | 'BOOKED'
    | 'SLOT_UNAVAILABLE'
    | 'ALREADY_USED'
    | 'EXPIRED'
    | 'INVALID'
    | 'NOT_ELIGIBLE';

export interface IVerifyBookingRequest {
    token: string;
    password: string;
}

/**
 * What following the link came to. `auth` is there for BOOKED and SLOT_UNAVAILABLE, and never
 * for a replayed link.
 */
export interface IVerifyBookingResponse {
    outcome: BookingOutcome;
    appointment?: IAppointment | null;
    serviceId?: number | null;
    therapistId?: number | null;
    auth?: AuthResponse | null;
    message?: string | null;
}
